import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Axios instance with cookie support
const http = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach JWT (for team/admin) automatically
http.interceptors.request.use((config) => {
  const token = localStorage.getItem("ch_jwt");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Emergent OAuth, AuthCallback handles it — skip /me check
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginInternal = async (email, password) => {
    const { data } = await http.post("/auth/login", { email, password });
    localStorage.setItem("ch_jwt", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const completeGoogleLogin = async (session_id) => {
    const { data } = await http.post("/auth/google/session", { session_id });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await http.post("/auth/logout");
    } catch {}
    localStorage.removeItem("ch_jwt");
    setUser(null);
  };

  const value = { user, loading, checkAuth, loginInternal, completeGoogleLogin, logout, http };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export { http };
